// Google Analytics 4, loaded only after the visitor allows it. The choice is
// a cookie on the parent domain, so one answer covers svgmascot.com and
// app.svgmascot.com. Keep in sync with apps/landing/src/lib/analytics.ts.
//
// Never send prompts, emails or other personal data as event parameters.

const CONSENT_COOKIE = "sm_consent";
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // 180 days, then we ask again

export type Consent = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** svgmascot.com in production (shared with the app), the host elsewhere. */
function cookieDomain(): string | null {
  const host = window.location.hostname;
  return host === "svgmascot.com" || host.endsWith(".svgmascot.com")
    ? "svgmascot.com"
    : null;
}

export function readConsent(): Consent | null {
  const match = document.cookie.match(/(?:^|; )sm_consent=(granted|denied)/);
  return match ? (match[1] as Consent) : null;
}

export function writeConsent(consent: Consent): void {
  const parts = [
    `${CONSENT_COOKIE}=${consent}`,
    `max-age=${CONSENT_MAX_AGE}`,
    "path=/",
    "samesite=lax",
  ];
  const domain = cookieDomain();
  if (domain) parts.push(`domain=${domain}`, "secure");
  document.cookie = parts.join("; ");
}

/**
 * Removes the cookies GA set. Runs when the visitor withdraws consent and on
 * every later load with consent denied, since a running gtag.js can write
 * them once more before the page unloads.
 */
export function clearAnalyticsCookies(): void {
  window.gtag?.("consent", "update", { analytics_storage: "denied" });
  const domain = cookieDomain();
  for (const c of document.cookie.split("; ")) {
    const name = c.split("=")[0];
    if (name !== "_ga" && !name.startsWith("_ga_")) continue;
    document.cookie = `${name}=; max-age=0; path=/`;
    if (domain)
      document.cookie = `${name}=; max-age=0; path=/; domain=${domain}`;
  }
}

export interface StartOptions {
  /** False when the caller sends page views itself (the SPA). */
  sendPageView: boolean;
  /** Overrides the page URL GA would read from the address bar. */
  pageLocation?: string;
}

/** Loads gtag.js once. Call only when consent is "granted". */
export function startAnalytics(measurementId: string, opts: StartOptions) {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js reads the Arguments object, not an array.
    window.dataLayer!.push(arguments);
  };
  // Analytics only: no advertising storage or ad personalisation.
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: opts.sendPageView,
    ...(opts.pageLocation ? { page_location: opts.pageLocation } : {}),
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

/** Sends a GA4 event; a no-op until analytics has started. */
export function track(
  name: string,
  params: Record<string, string | number> = {}
): void {
  window.gtag?.("event", name, params);
}

// ---- App only ----------------------------------------------------------

/** GA4 measurement ID (G-…); unset leaves analytics and the banner off. */
export const measurementId: string | undefined = import.meta.env
  .VITE_GA_MEASUREMENT_ID as string | undefined;

/**
 * The page URL GA receives: no query string or hash (they can carry checkout
 * and auth parameters), and one path per screen rather than one per project.
 */
export function analyticsPath(pathname: string): string {
  return pathname.replace(/^\/projects\/[^/]+/, "/projects/:id");
}

let lastPath: string | null = null;

/** Sends a page view for an SPA navigation; repeated paths are ignored. */
export function trackPageView(pathname: string): void {
  if (!window.gtag) return;
  const path = analyticsPath(pathname);
  if (path === lastPath) return;
  lastPath = path;
  const location = window.location.origin + path;
  window.gtag("set", { page_location: location });
  window.gtag("event", "page_view", { page_location: location });
}
